import React, { useMemo } from 'react';
import styles from './ClockView.module.scss';
import { Task, TaskWithState, TaskState } from '../../types';
import { AnalogClock } from '../AnalogClock/AnalogClock';
import { useNextUp } from '../../hooks/useNextUp';

interface ClockViewProps {
  time: Date;
  tasks: Task[];
  completedTaskIds?: string[];
  onTaskClick?: (task: TaskWithState) => void;
  // Settings props
  themeId?: string;
  showPastTasks?: boolean;
  showMinuteTicks?: boolean;
  taskSortingMode?: 'default' | 'layered';
}

export const ClockView: React.FC<ClockViewProps> = ({ 
  time, 
  tasks,
  completedTaskIds = [],
  onTaskClick,
  // Settings with defaults
  themeId = 'kids_light',
  showPastTasks = true,
  showMinuteTicks = false,
  taskSortingMode = 'default'
}) => {
  // Convert Task[] to TaskWithState[] for the clock
  const tasksWithState: TaskWithState[] = useMemo(() => {
    const now = time.getTime();
    
    // Helper to parse time string (HH:MM) into Date for today
    const getTaskDate = (timeStr: string, baseDate: Date) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      const date = new Date(baseDate);
      date.setHours(hours, minutes, 0, 0);
      return date;
    };

    return tasks.map(task => {
      const startTime = getTaskDate(task.start, time);
      let endTime = getTaskDate(task.end, time);
      
      // Handle crossing midnight
      if (endTime < startTime) {
        endTime.setDate(endTime.getDate() + 1);
      }

      const startMs = startTime.getTime();
      const endMs = endTime.getTime();
      
      let state = TaskState.UPCOMING;
      let opacity = 1.0;
      let shouldPulse = false;
      const fadePastDueMinutes = 20;

      if (completedTaskIds.includes(task.id)) {
        state = TaskState.COMPLETED;
        opacity = 0.5;
      } else if (now >= startMs && now < endMs) {
        state = TaskState.ACTIVE;
        shouldPulse = true;
      } else if (now >= endMs) {
        // Past due logic
        const minsPast = (now - endMs) / 60000;
        if (minsPast <= fadePastDueMinutes) {
          state = TaskState.PAST_DUE_WINDOW;
          opacity = Math.max(0, 1 - (minsPast / fadePastDueMinutes));
        } else {
          state = TaskState.GONE;
          opacity = 0;
        }
      }

      return {
        ...task,
        state,
        isCompleted: state === TaskState.COMPLETED,
        opacity,
        startTime,
        endTime,
        shouldPulse
      };
    }).filter(t => t.state !== TaskState.GONE);
  }, [tasks, time, completedTaskIds]);

  // Get next up task info
  const nextTask = useNextUp(tasksWithState, time, 60);

  // Handle task click from clock
  const handleTaskClick = (task: TaskWithState) => {
    onTaskClick?.(task);
  };

  return (
    <div className={styles.container}>
      <div className={styles.clockWrapper}>
        <AnalogClock
          currentTime={time}
          tasks={tasksWithState}
          nextTask={nextTask}
          onChipClick={() => {}}
          themeId={themeId}
          onTaskClick={handleTaskClick}
          showPastTasks={showPastTasks}
          showMinuteTicks={showMinuteTicks}
          taskSortingMode={taskSortingMode}
        />
      </div>

      {/* Task Info Display Below Clock */}
      <div className={styles.taskInfo}>
        {nextTask.task ? (
          <div className={styles.nextTaskChip}>
            <span className={styles.taskIcon}>{nextTask.task.icon}</span>
            <div className={styles.taskDetails}>
              <span className={styles.taskLabel}>{nextTask.task.label}</span>
              <span 
                className={styles.taskCountdown}
                style={{ color: nextTask.task.color }}
              >
                {nextTask.countdownText}
              </span>
            </div>
          </div>
        ) : (
          <div className={styles.noTask}>
            <span className={styles.noTaskText}>No upcoming tasks</span>
          </div>
        )}
      </div>
    </div>
  );
};
